// Angular
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { RouterModule } from '@angular/router';

/* External Modules */
import { AlertModule } from 'ngx-bootstrap/alert';
import { ModalModule } from 'ngx-bootstrap/modal';
import { ButtonsModule } from 'ngx-bootstrap/buttons';
import { CarouselModule } from 'ngx-bootstrap/carousel';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { PaginationModule } from 'ngx-bootstrap/pagination';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { ProgressbarModule } from 'ngx-bootstrap/progressbar';
import { RatingModule } from 'ngx-bootstrap/rating';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { TimepickerModule } from 'ngx-bootstrap/timepicker';
import { SortableModule } from 'ngx-bootstrap/sortable';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { BreadcrumbModule } from 'xng-breadcrumb';

/** Setup Highcharts */
import Highcharts from 'highcharts';
import highchartTreemap from 'highcharts/modules/treemap';
highchartTreemap(Highcharts);

// This module
import { DataTableComponent } from './datatable/data-table.component';
import { GlobalNavigationComponent } from './navigation/global-navigation.component';
import { GlobalFacilitySelectorComponent } from './navigation/global-facility-selector.component';
import { HideGlobalNavDirective } from './navigation/hide-global-nav.directive';
import { LoginComponent } from './login/login.component';
import { AccessDeniedComponent } from './login/access-denied.component';
import { PageNotFoundComponent } from './navigation/page-not-found.component';
import { ResizeDirective } from './resize.directive';
import { SubNavigationComponent } from './navigation/sub-navigation.component';
import { StickSubNavDirective } from './navigation/stick-sub-nav.directive';
import { KpiCardComponent } from './kpi-card/kpi-card.component';
import { DateRangeComponent } from './date/date-range-selector.component';
import { NotificationsTableComponent } from './notification-table/notifications-table.component';
import { NotificationFacilityListComponent } from './notification-facility-list/notification-facility-list.component';
import { PresetDateRangeComponent } from './date/preset-date-range-selector.component';
import { FeedbackComponent } from './feedback/feedback.component';
import { MyDatePipe } from './date/date.pipe';
import { MyDateTimePipe } from './date/date-time.pipe';
import { MyUserRolePipe } from '../core/user/user';
import { SingleFacilitySelectorComponent } from "./single-facilty-selector/single-facility-selector";
import { MessageBoxComponent } from "./message-box-modal/message-box-modal.component";
import { ChangePasswordComponent } from "./changePassword/change-password.component";
import { HomeSidebarComponent } from "./home-sidebar/home-sidebar.component";
import { PsychotropicDashSummaryDonutComponent } from "../reports/summaries/psychotropic-summary/psychotropic-summary/psychotropic-summary-donut-chart.component";
import { ControlledMedSummaryDonutComponent } from "../reports/summaries/control-medication/controlled-med-summary/controlled-med-summary-donut-chart.component";
import { TotalCostSummaryChartsComponent } from '../reports/summaries/pharmacy-cost-summary/total-cost/total-cost-charts.component';
import { AntibioticsSummaryDonutChartComponent } from "../reports/summaries/medication-summary/antibiotics/antibiotics-summary-donut.component";
import { MedicalRecordsModalComponent } from "../reports/medical-records-modal/medical-records-modal.component";
import { PharmacyCostByTypeChart } from "../reports/summaries/pharmacy-cost-summary/total-cost/pharmacy-cost-by-type-chart.component";
import { CostPerRxChartsComponent } from "../reports/summaries/pharmacy-cost-summary/corporate-dashboard/cost-per-rx-chart.component";
import { TotalPharmacyCostChartsComponent } from "../reports/summaries/pharmacy-cost-summary/corporate-dashboard/total-pharmacy-cost-charts.component";
import { CorporateDashBillingSummaryChart } from "../reports/summaries/pharmacy-cost-summary/corporate-dashboard/corporate-dash-billing-summary-chart.component";
import { NonCoveredChargesChartComponent } from "../reports/summaries/pharmacy-cost-summary/corporate-dashboard/non-covered-charges-chart.component";
import { LeaveOfAbsenceReportModal } from "../reports/leave-of-absence-report-modal/leave-of-absence-report-modal.component";
import { MedicalDischargeReport } from "../reports/medical-discharge/medical-discharge-report.component";
import { IterablePipe } from "../reports/medication-orders-search/mapPipe";
import { MyDateMonthYearPipe } from './date/date-month-year.pipe';
import { InputNumberComponent } from './input-number/input-number-component';
import { MedicationFallRiskModal } from '../reports/medication-fall-risk-modal/medication-fall-risk-modal.component';
import { AutofocusDirective } from './auto-focus.directive';
import { CreditDetailModal } from '../reports/credit-detail-modal/credit-detail-modal.component';
import { AuthenticationHandlerComponent } from './message-box-modal/authentication-handler.component';
import { DropDownKeyboardNavDirective } from './dropdown-keyboard-nav.directive';
import { ScrollableListMessageBoxModal } from './message-box-modal/scrollable-list-message-box-modal/scrollable-list-message-box-modal.component';
import { BillingSummaryModal } from '../reports/billing-summary-modal/billing-summary-modal.component';
import { BillingDetailModal } from '../reports/billing-detail-modal/billing-detail-modal.component';
import { BillingHistoryModal } from '../reports/billing-history-modal/billing-history-modal.component';
import { ActiveControlledMedicationsModal, OrderTypePage, PrescriberPage} from '../reports/active-controlled-medications-modal';
import { NursingStationPage } from '../reports/wizard-pages';
import { DiscontinuedControlledMedicationsModal } from '../reports/discontinued-controlled-medicatons-modal/discontinued-controlled-medications-modal.component';
import { ResidentDiagnosisModal } from '../reports/resident-diagnosis/resident-diagnosis-modal.component';
import { RoutineNonUsageModal } from '../reports/routine-non-usage-modal/routine-non-usage-modal.component';
import { ResidentAllergyModal } from '../reports/resident-allergy-modal/resident-allergy.component';
import { DispensedControlledMedications } from '../reports/dispensed-controlled-medications/dispensed-controlled-medications';
import { NewAndDiscontinuedOrdersModal } from '../reports/new-and-discontinued-orders-modal/new-and-discontinued-orders-modal.component';
import { ResidentLiteralOrderModal } from '../reports/resident-literal-orders-modal/resident-literal-order.component';
import { MedicalBenefitOrderModal } from '../reports/medical-benefit-order-modal/medical-benefit-order.component';
import { ExcelSwapPipe } from './string-pipe/excelswap.pipe';
import { FileUploadComponent } from './file-upload/file-upload.component';
import { AddTrailingWhitespace } from './string-pipe/add-trailing-whitespace.pipe';
import { UnbilledChargesModal } from '../reports/unbilled-charges-modal/unbilled-charges-modal.component';


/** Components & Directives to declare and export */
const MY_EXPORTED_COMPONENTS = [
    DataTableComponent, DateRangeComponent, FeedbackComponent,
    GlobalNavigationComponent, GlobalFacilitySelectorComponent, HideGlobalNavDirective,
    KpiCardComponent, LoginComponent, AccessDeniedComponent, NotificationsTableComponent,
    NotificationFacilityListComponent, PageNotFoundComponent, PresetDateRangeComponent,
    ResizeDirective, SubNavigationComponent, SingleFacilitySelectorComponent, HomeSidebarComponent,
    PsychotropicDashSummaryDonutComponent, AntibioticsSummaryDonutChartComponent,
    TotalCostSummaryChartsComponent, MedicalRecordsModalComponent, LeaveOfAbsenceReportModal, MedicalDischargeReport,
    PharmacyCostByTypeChart, CostPerRxChartsComponent, TotalPharmacyCostChartsComponent, CorporateDashBillingSummaryChart,
    NonCoveredChargesChartComponent,MessageBoxComponent,ChangePasswordComponent, InputNumberComponent, MedicationFallRiskModal, AutofocusDirective,
    CreditDetailModal, AuthenticationHandlerComponent, DropDownKeyboardNavDirective, ScrollableListMessageBoxModal, BillingSummaryModal, 
    BillingDetailModal, BillingHistoryModal, ActiveControlledMedicationsModal, OrderTypePage, PrescriberPage, NursingStationPage, ResidentDiagnosisModal, 
    DiscontinuedControlledMedicationsModal, RoutineNonUsageModal, DispensedControlledMedications,
    ResidentAllergyModal, 
    NewAndDiscontinuedOrdersModal,
    ResidentLiteralOrderModal, 
    FileUploadComponent, MedicalBenefitOrderModal, ControlledMedSummaryDonutComponent, UnbilledChargesModal
];

/** Pipes to declare and export */
const MY_EXPORTED_PIPES = [
    MyDatePipe, MyDateTimePipe, MyDateMonthYearPipe, MyUserRolePipe, IterablePipe,ExcelSwapPipe, AddTrailingWhitespace
];

/**
 * The Shared module holds the common components, directives, and pipes and shares them with the modules that need them.
 *
 * Notes:
 * - Services and related models go in the Core module, not here.
 * - If a component, directive, or pipe is only needed by a single feature, it belongs within that feature's module.
 *
 * @see https://angular.io/docs/ts/latest/guide/ngmodule.html#!#shared-module
 */
@NgModule({
    imports: [
        // Angular stuff
        CommonModule,
        FormsModule,
        RouterModule,
        // Bootstrap stuff
        AlertModule,
        ButtonsModule,
        CarouselModule,
        CollapseModule,
        BsDatepickerModule.forRoot(),
        BsDropdownModule,
        ModalModule.forRoot(),
        PaginationModule,
        PopoverModule,
        ProgressbarModule.forRoot(),
        RatingModule,
        TabsModule,
        TimepickerModule,
        SortableModule,
        TooltipModule,
        TypeaheadModule,
        // Application modules
        // Other external modules
        NgxDatatableModule,
        BreadcrumbModule
    ],

    declarations: [
        StickSubNavDirective,
        ...MY_EXPORTED_COMPONENTS,
        ...MY_EXPORTED_PIPES
    ],

    exports: [
        // Shared module stuff;
        ...MY_EXPORTED_COMPONENTS,
        ...MY_EXPORTED_PIPES,

        // Angular stuff
        CommonModule, FormsModule,
        // Bootstrap stuff
        AlertModule, ButtonsModule, CarouselModule, CollapseModule, BsDatepickerModule,
        BsDropdownModule, ModalModule, PaginationModule, PopoverModule, ProgressbarModule, RatingModule,
        TabsModule, TimepickerModule, SortableModule, TooltipModule, TypeaheadModule,
        // Other external modules
        NgxDatatableModule, BreadcrumbModule
    ]
})
export class SharedModule {
}
